import React, { FC } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MobileMenuNav from './MobileMenuNav';

import {
  StatsChartIcon as DashboardIcon,
  SwapHorizontalIcon as TransactionsIcon,
  BeneficiariesIcon,
  PhonePortraitOutlineIcon as TerminalsIcon,
  PersonIcon as StaffIcon,
  LocationIcon,
  QRcodeIcon,
  PayrollIcon,
  LogOutIcon,
  SettingsIcon as ProfileIcon,
  CashIcon,
  // ShieldCheckmarkIcon as KycIcon,
  // GlobeIcon as InternetSalesIcon,
  // SettingsIcon as ProfileIcon,
} from '~/img';
import GridLayout from '../GridLayout';
import { auth } from '~/api/axios';
import { WalletOutlined } from '@ant-design/icons';
import {
  employees,
  terminals,
  paymentDetails,
  locations,
  transactions,
  beneficiaries,
  accounts,
  payrolls,
  sendMoney,
} from '~/routes';
import { usePermissions } from '~/hooks';
import useIsSendMoneyAvailable from '~/hooks/useIsSendMoneyAvailable';

interface Props {
  onClose: () => void;
}

const MobileMenu: FC<Props> = (props) => {
  const { onClose } = props;
  const history = useHistory();
  const { t } = useTranslation();
  const handleNav = (route: string) => () => {
    history.push(route);
    onClose();
  };

  const onLogout = () => {
    auth.deleteToken();
    history.push('/login');
  };

  const location = useLocation();
  const selectedKeys = [location.pathname.split('/').slice(0, 2).join('/')];

  const hasSendMoneyFeature = useIsSendMoneyAvailable();

  const transactionsAccess = usePermissions('business.transactions.list');
  const terminalAccess = usePermissions('business.terminal.list');
  const employeeAccess = usePermissions('business.employee.list');
  const locationAccess = usePermissions('business.location.list');
  const paymentDetailsAccess = usePermissions('business.paymentDetails.list');
  const payrollsAccess = usePermissions('business.payrolls.list');
  const beneficiariesAccess = usePermissions('business.beneficiary.view');
  const accountsAccess = usePermissions('business.accounts.view');
  const sendMoneyAccess = usePermissions('business.directTransfer.create') && hasSendMoneyFeature;
  return (
    <Container>
      <GridLayout gutter={[16, 32]} xs={8}>
        <MobileMenuNav
          icon={<DashboardIcon />}
          name={t('dashboard')}
          onClick={handleNav('/')}
          active={selectedKeys.includes('/')}
        />
        {transactionsAccess && (
          <MobileMenuNav
            icon={<TransactionsIcon />}
            name={t('transactions')}
            onClick={handleNav(`/${transactions}`)}
            active={selectedKeys.includes(`/${transactions}`)}
          />
        )}
        {sendMoneyAccess && (
          <MobileMenuNav
            icon={<CashIcon />}
            name={t('sendMoney')}
            onClick={handleNav(`/${sendMoney}`)}
            active={selectedKeys.includes(`/${sendMoney}`)}
          />
        )}
        {beneficiariesAccess && (
          <MobileMenuNav
            icon={<BeneficiariesIcon />}
            name={t('beneficiaries')}
            onClick={handleNav(`/${beneficiaries}`)}
            active={selectedKeys.includes(`/${beneficiaries}`)}
          />
        )}
        {terminalAccess && (
          <MobileMenuNav
            icon={<TerminalsIcon />}
            name={t('terminals')}
            onClick={handleNav(`/${terminals}`)}
            active={selectedKeys.includes(`/${terminals}`)}
          />
        )}
        {employeeAccess && (
          <MobileMenuNav
            icon={<StaffIcon />}
            name={t('team')}
            onClick={handleNav(`/${employees}`)}
            active={selectedKeys.includes(`/${employees}`)}
          />
        )}
        {locationAccess && (
          <MobileMenuNav
            icon={<LocationIcon />}
            name={t('locations')}
            onClick={handleNav(`/${locations}`)}
            active={selectedKeys.includes(`/${locations}`)}
          />
        )}
        {paymentDetailsAccess && (
          <MobileMenuNav
            icon={<QRcodeIcon />}
            name={t('qrCodes')}
            onClick={handleNav(`/${paymentDetails}`)}
            active={selectedKeys.includes(`/${paymentDetails}`)}
          />
        )}
        {payrollsAccess && (
          <MobileMenuNav
            icon={<PayrollIcon />}
            name={t('payrolls')}
            onClick={handleNav(`/${payrolls}`)}
            active={selectedKeys.includes(`/${payrolls}`)}
          />
        )}
        {accountsAccess && (
          <MobileMenuNav
            icon={<WalletOutlined />}
            name={t('accounts')}
            onClick={handleNav(`/${accounts}`)}
            active={selectedKeys.includes(`/${accounts}`)}
          />
        )}
        <MobileMenuNav
          icon={<ProfileIcon />}
          name={t('profile')}
          onClick={handleNav('/profile')}
          active={selectedKeys.includes('/profile')}
        />
        <MobileMenuNav icon={<LogOutIcon />} name={t('logout')} onClick={onLogout} />
      </GridLayout>
    </Container>
  );
};

const Container = styled.div`
  padding: 32px 16px;
`;

export default MobileMenu;
